import React, { useMemo } from 'react';
import { Link } from 'react-router';
import { ListChecks } from 'lucide-react';
import { useLeagueReadiness } from '../hooks/useLeagueReadiness';
import { mapReadinessToSetupSummary } from '../leagueReadinessMapper';
import { lscPath } from '../routing';
import ErrorBanner from './ErrorBanner';
import LoadingState from './LoadingState';

export interface LeagueSetupSettingsCardProps {
  leagueId: string;
}

const LeagueSetupSettingsCard: React.FC<LeagueSetupSettingsCardProps> = ({ leagueId }) => {
  const { readiness, loading, error, authError, reload } = useLeagueReadiness(leagueId);

  const summary = useMemo(
    () => (readiness ? mapReadinessToSetupSummary(readiness) : null),
    [readiness]
  );

  const setupHref = lscPath(`/leagues/${leagueId}/setup`);

  return (
    <section className="card space-y-3" data-testid="league-setup-settings-card">
      <div className="flex items-start gap-3">
        <ListChecks className="h-5 w-5 text-primary-400 shrink-0 mt-0.5" aria-hidden />
        <div className="space-y-1">
          <h2 className="font-semibold text-white">League setup</h2>
          <p className="text-sm text-secondary-400">
            Finish setting up your league. Check subscription, schedule, members, Twitch, and
            automation readiness in one guided flow.
          </p>
        </div>
      </div>

      {loading ? <LoadingState message="Loading setup status…" /> : null}

      {!loading && authError === 'unauthorized' ? (
        <ErrorBanner message="Sign in to view league setup status." />
      ) : null}

      {!loading && authError === 'forbidden' ? (
        <ErrorBanner message="You do not have permission to view league setup status." />
      ) : null}

      {!loading && !authError && (error || !summary) ? (
        <div className="space-y-3">
          <ErrorBanner message={error ?? 'Unable to load setup status. Try again in a moment.'} />
          <button type="button" className="btn-secondary text-sm" onClick={reload}>
            Retry
          </button>
        </div>
      ) : null}

      {!loading && !authError && summary ? (
        <div className="space-y-3" data-testid="league-setup-settings-summary">
          {summary.showCompactReady ? (
            <p className="text-sm text-emerald-200">League ready — core setup is complete.</p>
          ) : (
            <p className="text-sm text-secondary-300">
              {summary.blockerCount} blocker{summary.blockerCount === 1 ? '' : 's'}
              {summary.warningCount > 0
                ? ` · ${summary.warningCount} warning${summary.warningCount === 1 ? '' : 's'}`
                : null}
            </p>
          )}
          <Link
            to={setupHref}
            className="btn-primary inline-flex text-sm"
            data-testid="league-setup-settings-cta"
          >
            Open setup wizard
          </Link>
        </div>
      ) : null}
    </section>
  );
};

export default LeagueSetupSettingsCard;
